import { useEffect, useMemo, useRef } from 'react';
import {
  createDispatchNoteReq,
  dispatch,
  DispatchNoteStatus,
  ErrorVariants,
  FileUploadOutput,
  getExtraDetails,
  getWorkspaceId,
  showToast,
  useAppSelector,
  useCreateDispatchNoteMutation,
  useUploadWorkspaceDocumentsMutation,
} from '@zonofi/zono-money-store';
import { useNavigate } from 'react-router-dom';
import { dispatch_Vehicle_Details } from '../../components';
import { validateFields } from '../../helper';
import moment from 'moment';
import { UploadToS3 } from '@zonofi/common';

interface UploadDispatchNoteProps {
  uploadedMedia: FileUploadOutput[];
  formData: Record<string, string | string[]>;
  channelId?: string;
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, string | string[]>>
  >;
  setOpenSections: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  isUpdateCustomer?: boolean;
  isEditMode?: boolean;
}

export const useUploadDispatchNote = ({
  uploadedMedia,
  formData,
  channelId,
  setOpenSections,
  setFormData,
  isUpdateCustomer,
  isEditMode,
}: UploadDispatchNoteProps) => {
  const customerDetails = useAppSelector(getExtraDetails);
  const { customerId = '' } = customerDetails;
  const navigate = useNavigate();
  const hasUploadedRef = useRef(false);

  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const [createDispatchNote, createDispatchNoteResponse] =
    useCreateDispatchNoteMutation();
  const [uploadDocument, uploadDocumentResponse] =
    useUploadWorkspaceDocumentsMutation();

  const handleChange = (key: string, value: string | string[]) => {
    setFormData((prev) => {
      if (key === 'totalBox') {
        return {
          ...prev,
          totalBox: value,
          totalWeight: value ? '' : prev.totalWeight,
        };
      }

      if (key === 'totalWeight') {
        return {
          ...prev,
          totalWeight: value,
          totalBox: value ? '' : prev.totalBox,
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleToggle = (key: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isFormValid = useMemo(() => {
    const areDispatchFieldsValid = validateFields(
      formData,
      dispatch_Vehicle_Details
    );

    return areDispatchFieldsValid && uploadedMedia.length > 0;
  }, [formData, uploadedMedia]);

  const onUploadDispatchNote = async (docUri?: string) => {
    const raw = formData.dispatchDate;

    const parsed = raw
      ? moment(raw, ['DD/MM/YYYY', moment.ISO_8601], true)
      : null;

    const dispatchDate =
      parsed && parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
    const payload = {
      ...(isEditMode
        ? {
            id: formData?.id,
            docs: { invoice: formData.docs[0] },
            status: DispatchNoteStatus.Dispatched,
          }
        : {}),
      inviteId: customerId,
      workspaceId: sellerWorkspaceId,
      channelId,
      dispatchDocNumber: formData.dispatchDocNo ?? '',
      dispatchDate: dispatchDate,
      numOfBoxes: formData.totalBox ?? '',
      totalWeightINKg: formData.totalWeight ?? '',
      transporterName: formData.transporterName ?? '',
      vehicleNumber: formData.vehicleNumber ?? '',
      driverName: formData.driverName ?? '',
      type: 'invoice',
      driverPhone: formData.driverPhone ?? '',
      destinationCity: formData.cityName ?? '',
      destinationZipCode: formData.Pincode ?? '',
      destinationAddress: formData.address ?? '',
      termsOfDelivery: formData.termsOfDelivery ?? '',
      ...(docUri ? { docs: { invoice: docUri } } : {}),
      orderIds: Array.isArray(formData?.orderIds)
        ? formData.orderIds.map(Number)
        : [],
      invoiceIds: formData.invoiceIds ?? [],
    };

    createDispatchNote(payload as createDispatchNoteReq);
  };

  useEffect(() => {
    if (
      createDispatchNoteResponse.isSuccess &&
      createDispatchNoteResponse.data
    ) {
      if (isEditMode) {
        setTimeout(() => {
          dispatch(
            showToast({
              message: 'Dispatch note created successfully',
              type: ErrorVariants.SUCCESS,
              canDisplay: true,
              responseStatus: 'Success',
            })
          );
        }, 200);
        navigate(-1);
      }

      // FIRST SUCCESS → upload files
      if (!isEditMode && uploadedMedia?.length > 0 && !hasUploadedRef.current) {
        hasUploadedRef.current = true;
        uploadFilesToS3(createDispatchNoteResponse?.data?.id);
        return;
      }

      // SECOND SUCCESS → final
      if (hasUploadedRef.current) {
        setTimeout(() => {
          dispatch(
            showToast({
              message: 'Dispatch note created successfully',
              type: ErrorVariants.SUCCESS,
              canDisplay: true,
              responseStatus: 'Success',
            })
          );
        }, 200);
        navigate(-1);
      }
    }
  }, [createDispatchNoteResponse]);

  const uploadFilesToS3 = async (dispatchId: string) => {
    let presignedUrls: string[] = [];
    try {
      const uploadPromises = uploadedMedia.map(async (item) => {
        const response = await uploadDocument({
          workspaceId: sellerWorkspaceId,
          documentType: 'dispatch',
          entityId: dispatchId,
          mimeType: item?.file?.type,
          subType: 'invoice',
        }).unwrap();

        if (response?.presignedUrl) {
          const presignedUrl = response.presignedUrl;
          presignedUrls.push(presignedUrl);

          await UploadToS3(presignedUrl, {
            uri: item.uri!.toString(),
            type: item.file?.type,
          });
        }
      });

      await Promise.all(uploadPromises);
    } catch (error) {
      dispatch(
        showToast({
          canDisplay: true,
          message: 'Failed to upload file(s)',
          responseStatus: 'Warning',
          type: ErrorVariants.WARNING,
        })
      );
    }

    if (presignedUrls.length > 0) {
      onUploadDispatchNote(presignedUrls[0]);
    }
  };

  useEffect(() => {
    if (createDispatchNoteResponse.isError) {
      let errorMessage = 'Failed to create dispatch note';

      const error = createDispatchNoteResponse.error;
      if (error) {
        if ('data' in error) {
          const fetchError = error as any;
          errorMessage =
            (fetchError.data as { message?: string })?.message || errorMessage;
        } else if ('message' in error) {
          errorMessage = error.message || errorMessage;
        }
      }

      dispatch(
        showToast({
          message: errorMessage,
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [createDispatchNoteResponse]);

  return {
    handleChange,
    handleToggle,
    isFormValid,
    onUploadDispatchNote,
    isLoading:
      createDispatchNoteResponse?.isLoading || uploadDocumentResponse.isLoading,
  };
};
